import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container, Box, Typography, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Stack, Chip, Divider, IconButton
} from "@mui/material";
import { BsFileEarmarkSpreadsheet, BsPrinter, BsCheckCircleFill, BsReceipt, BsArrowRightCircle } from "react-icons/bs";
import { MdClose } from "react-icons/md";
import { WORKALL, INVOICE, INVOICEALL } from "../../services/actions";
import axios from "axios";
import VoiceMicButton from "../../components/VoiceMicButton";

export default function InvoiceView() {
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState(0);
  const [works, setWorks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedWork, setSelectedWork] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [openPrint, setOpenPrint] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Form states
  const [amount, setAmount] = useState("");
  const [taxPct, setTaxPct] = useState("18.00");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");

  const auth = useSelector((state) => state.loginState?.auth);
  const workallState = useSelector((state) => state.workallState);
  const invoiceallState = useSelector((state) => state.invoiceallState);
  const invoiceState = useSelector((state) => state.invoiceState);

  const getApiUrl = (endpoint) => {
    let base = process.env.REACT_APP_URL || "http://localhost:8000/api/";
    if (!base.endsWith("/")) base += "/";
    let p = endpoint;
    if (p.startsWith("/")) p = p.slice(1);
    return base + p;
  };

  const loadData = () => {
    if (!auth?.token) return;
    const headers = { Authorization: `Bearer ${auth.token}` };

    // Fetch Completed Works
    axios.get(getApiUrl('work/0/?filter_by={"status":"completed"}'), { headers })
      .then((res) => {
        const raw = res.data?.data || res.data;
        const list = Array.isArray(raw) ? raw : (raw?.results || []);
        setWorks(list);
      }).catch(console.error);

    // Fetch Invoices
    axios.get(getApiUrl('invoice/'), { headers })
      .then((res) => {
        const raw = res.data?.data || res.data;
        const list = Array.isArray(raw) ? raw : (raw?.results || []);
        setInvoices(list);
      }).catch(console.error);
  };

  useEffect(() => {
    loadData();
  }, [auth?.token]);

  useEffect(() => {
    if (invoiceState?.success) {
      setOpenCreate(false);
      setSelectedWork(null);
      setAmount("");
      setNotes("");
      loadData();
    }
  }, [invoiceState]);

  const handleOpenCreate = (work) => {
    setSelectedWork(work);
    setAmount("1000.00");
    const nextMonth = new Date();
    nextMonth.setDate(nextMonth.getDate() + 15);
    setDueDate(nextMonth.toISOString().split("T")[0]);
    setOpenCreate(true);
  };

  const handleCreateInvoice = () => {
    if (!selectedWork || !amount) return;
    const baseAmt = parseFloat(amount) || 0;
    const tax = baseAmt * (parseFloat(taxPct) / 100);
    const total = baseAmt + tax;
    const invNum = `INV-${Math.floor(100000 + Math.random() * 900000)}`;

    const payload = {
      work: selectedWork.id,
      invoice_number: invNum,
      amount: baseAmt.toFixed(2),
      tax_percentage: parseFloat(taxPct).toFixed(2),
      total_amount: total.toFixed(2),
      due_date: dueDate || null,
      notes: notes
    };

    dispatch({ type: `${INVOICE}_POST`, payload });
  };

  const handleOpenPrint = (inv) => {
    setSelectedInvoice(inv);
    setOpenPrint(true);
  };

  const triggerBrowserPrint = () => {
    window.print();
  };

  const baseAmtNum = parseFloat(amount) || 0;
  const calcTax = baseAmtNum * (parseFloat(taxPct) / 100);
  const calcTotal = baseAmtNum + calcTax;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', color: '#fff', mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={3}>
          <Box sx={{ p: 2, borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.1)', display: 'flex' }}>
            <BsReceipt size={36} color="#38bdf8" />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight="800" sx={{ letterSpacing: '-0.5px' }}>
              Billing & Invoices Hub
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8', mt: 0.5 }}>
              Review completed client deliverables and issue formal GST invoices.
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ '& .MuiTab-root': { fontWeight: '700', fontSize: '1rem', textTransform: 'none' } }}>
          <Tab label={`Completed Works Ready for Billing (${works.length})`} />
          <Tab label={`Issued Invoices (${invoices.length})`} />
        </Tabs>
      </Box>

      {tabIndex === 0 && (
        <TableContainer component={Paper} sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: '800', color: '#475569' }}>Work ID & Title</TableCell>
                <TableCell sx={{ fontWeight: '800', color: '#475569' }}>Client</TableCell>
                <TableCell sx={{ fontWeight: '800', color: '#475569' }}>Service</TableCell>
                <TableCell sx={{ fontWeight: '800', color: '#475569' }}>Completed On</TableCell>
                <TableCell sx={{ fontWeight: '800', color: '#475569' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: '800', color: '#475569' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {works.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                    No completed works pending invoice creation.
                  </TableCell>
                </TableRow>
              ) : (
                works.map((row) => {
                  const isAlreadyInvoiced = invoices.some(inv => Number(inv.work) === Number(row.id));
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: '700' }}>#{row.id} - {row.work_name}</TableCell>
                      <TableCell>{row.client_name || row.client}</TableCell>
                      <TableCell>{row.service_name || row.service}</TableCell>
                      <TableCell>{row.updated_at ? new Date(row.updated_at).toLocaleDateString() : 'Recently'}</TableCell>
                      <TableCell>
                        <Chip icon={<BsCheckCircleFill />} label="Completed" color="success" size="small" sx={{ fontWeight: '600' }} />
                      </TableCell>
                      <TableCell align="right">
                        {isAlreadyInvoiced ? (
                          <Chip label="Invoiced" color="primary" variant="outlined" size="small" />
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleOpenCreate(row)}
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: '700', bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' } }}
                          >
                            Generate Invoice
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tabIndex === 1 && (
        <TableContainer component={Paper} sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: '800', color: '#475569' }}>Invoice #</TableCell>
                <TableCell sx={{ fontWeight: '800', color: '#475569' }}>Deliverable Work</TableCell>
                <TableCell sx={{ fontWeight: '800', color: '#475569' }}>Client</TableCell>
                <TableCell sx={{ fontWeight: '800', color: '#475569' }}>Issue Date</TableCell>
                <TableCell sx={{ fontWeight: '800', color: '#475569' }}>Total Amount</TableCell>
                <TableCell align="right" sx={{ fontWeight: '800', color: '#475569' }}>Preview / Print</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                    No invoices generated yet.
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ fontWeight: '800', color: '#0ea5e9' }}>{inv.invoice_number}</TableCell>
                    <TableCell sx={{ fontWeight: '600' }}>{inv.work_name || `Work #${inv.work}`}</TableCell>
                    <TableCell>{inv.client_name || '-'}</TableCell>
                    <TableCell>{inv.issue_date}</TableCell>
                    <TableCell sx={{ fontWeight: '800', color: '#16a34a' }}>₹{inv.total_amount}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<BsPrinter />}
                        onClick={() => handleOpenPrint(inv)}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: '700' }}
                      >
                        View / Print
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── CREATE INVOICE MODAL ── */}
      <Dialog 
        open={openCreate} 
        onClose={() => setOpenCreate(false)} 
        maxWidth="sm" 
        fullWidth 
        PaperProps={{ 
          sx: { 
            borderRadius: '16px', 
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          } 
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', pb: 1.5, pt: 2, px: 3, backgroundColor: '#ffffff' }}>
          <Box>
            <Typography variant="h6" fontWeight="800" color="#0f172a" fontSize={18}>
              Generate Formal Invoice
            </Typography>
            <Typography variant="caption" color="#64748b" sx={{ display: 'block', mt: 0.3 }}>
              For: <strong>{selectedWork?.work_name}</strong> (#{selectedWork?.id})
            </Typography>
          </Box>
          <IconButton onClick={() => setOpenCreate(false)} size="small" sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>
            <MdClose size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, backgroundColor: '#f8fafc' }}>
          <Stack spacing={2.2}>
            {/* Row 1: Base Amount and Tax % */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography component="label" sx={{ fontSize: '13px', fontWeight: 600, color: '#334155', mb: 0.6, display: 'block' }}>
                  Base Amount (₹) <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <input
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  style={{
                    width: '100%',
                    height: '42px',
                    padding: '0 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    fontSize: '14px',
                    color: '#0f172a',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography component="label" sx={{ fontSize: '13px', fontWeight: 600, color: '#334155', mb: 0.6, display: 'block' }}>
                  Tax / GST (%)
                </Typography>
                <input
                  type="number"
                  placeholder="18"
                  value={taxPct}
                  onChange={(e) => setTaxPct(e.target.value)}
                  style={{
                    width: '100%',
                    height: '42px',
                    padding: '0 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    fontSize: '14px',
                    color: '#0f172a',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </Box>
            </Box>

            {/* Row 2: Due Date */}
            <Box>
              <Typography component="label" sx={{ fontSize: '13px', fontWeight: 600, color: '#334155', mb: 0.6, display: 'block' }}>
                Due Date
              </Typography>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{
                  width: '100%',
                  height: '42px',
                  padding: '0 12px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#ffffff',
                  fontSize: '14px',
                  color: '#0f172a',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </Box>

            {/* Price Summary Breakdown Card */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                <Typography variant="body2" color="#64748b">Base Price:</Typography>
                <Typography variant="body2" fontWeight="600" color="#1e293b">₹{baseAmtNum.toFixed(2)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                <Typography variant="body2" color="#64748b">GST ({taxPct}%):</Typography>
                <Typography variant="body2" fontWeight="600" color="#1e293b">₹{calcTax.toFixed(2)}</Typography>
              </Stack>
              <Divider sx={{ my: 1, borderColor: '#e2e8f0' }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" fontWeight="700" color="#0f172a">Total Billed:</Typography>
                <Typography variant="subtitle1" fontWeight="800" color="#2563eb">₹{calcTotal.toFixed(2)}</Typography>
              </Stack>
            </Paper>

            {/* Row 3: Billing Notes */}
            <Box>
              <Typography component="label" sx={{ fontSize: '13px', fontWeight: 600, color: '#334155', mb: 0.6, display: 'block' }}>
                Billing Notes / Payment Terms
              </Typography>
              <div style={{ position: 'relative' }}>
                <textarea
                  rows={3}
                  placeholder="e.g. Please pay within 15 days via NEFT/Bank Transfer."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 38px 10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    fontSize: '13.5px',
                    color: '#0f172a',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                />
                <div style={{ position: 'absolute', right: 8, top: 10, zIndex: 5 }}>
                  <VoiceMicButton
                    initialText={notes}
                    onTranscript={(text) => setNotes(text)}
                    size={16}
                  />
                </div>
              </div>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, px: 3, borderTop: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
          <Button onClick={() => setOpenCreate(false)} sx={{ fontWeight: '600', color: '#64748b', textTransform: 'none', borderRadius: '8px' }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCreateInvoice} 
            sx={{ 
              borderRadius: '8px', 
              px: 3.5, 
              py: 1, 
              fontWeight: '600', 
              textTransform: 'none',
              backgroundColor: '#2563eb',
              '&:hover': { backgroundColor: '#1d4ed8' }
            }}
          >
            Issue Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── PRINT / PREVIEW MODAL ── */}
      <Dialog open={openPrint} onClose={() => setOpenPrint(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="800">Invoice Document Preview</Typography>
          <Button variant="contained" startIcon={<BsPrinter />} onClick={triggerBrowserPrint} sx={{ borderRadius: '8px', fontWeight: '700' }}>
            Print Document
          </Button>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 5, bgcolor: '#fff', color: '#000' }}>
          {selectedInvoice && (
            <Box id="printable-area" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 6 }}>
                <Box>
                  <Typography variant="h4" fontWeight="900" color="#0f172a">INVOICE</Typography>
                  <Typography variant="h6" color="#0ea5e9" fontWeight="700">{selectedInvoice.invoice_number}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Issued On: {selectedInvoice.issue_date}<br />
                    Due Date: {selectedInvoice.due_date || 'Payable on receipt'}
                  </Typography>
                </Box>
                <Box align="right">
                  <Typography variant="h6" fontWeight="800">TASK MANAGEMENT CORP</Typography>
                  <Typography variant="body2" color="text.secondary">
                    123 Innovation Way, Tech District<br />
                    GSTIN: 29AAAAA0000A1Z5<br />
                    billing@tms.plakal.in
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 4 }} />

              <Stack direction="row" justifyContent="space-between" sx={{ mb: 5 }}>
                <Box>
                  <Typography variant="caption" fontWeight="700" color="text.secondary">BILLED TO:</Typography>
                  <Typography variant="h6" fontWeight="800">{selectedInvoice.client_name || 'Client Corp'}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedInvoice.client_email || ''}</Typography>
                </Box>
                <Box align="right">
                  <Typography variant="caption" fontWeight="700" color="text.secondary">DELIVERABLE REFERENCE:</Typography>
                  <Typography variant="body1" fontWeight="700">{selectedInvoice.work_name || `Work #${selectedInvoice.work}`}</Typography>
                </Box>
              </Stack>

              <Table sx={{ mb: 4, border: '1px solid #cbd5e1' }}>
                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: '800' }}>Description</TableCell>
                    <TableCell align="right" sx={{ fontWeight: '800' }}>Tax %</TableCell>
                    <TableCell align="right" sx={{ fontWeight: '800' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ py: 3 }}>
                      <Typography fontWeight="700">{selectedInvoice.work_name}</Typography>
                      <Typography variant="caption" color="text.secondary">Professional execution and delivery of agreed milestone.</Typography>
                    </TableCell>
                    <TableCell align="right">{selectedInvoice.tax_percentage}%</TableCell>
                    <TableCell align="right" sx={{ fontWeight: '700' }}>₹{selectedInvoice.amount}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 6 }}>
                <Box sx={{ width: 280 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                    <Typography color="text.secondary">Subtotal:</Typography>
                    <Typography fontWeight="600">₹{selectedInvoice.amount}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                    <Typography color="text.secondary">GST ({selectedInvoice.tax_percentage}%):</Typography>
                    <Typography fontWeight="600">
                      ₹{(parseFloat(selectedInvoice.total_amount) - parseFloat(selectedInvoice.amount)).toFixed(2)}
                    </Typography>
                  </Stack>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                    <Typography variant="h6" fontWeight="800">Total Billed:</Typography>
                    <Typography variant="h6" fontWeight="800" color="#0ea5e9">₹{selectedInvoice.total_amount}</Typography>
                  </Stack>
                </Box>
              </Box>

              {selectedInvoice.notes && (
                <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: '8px', borderLeft: '4px solid #0ea5e9' }}>
                  <Typography variant="caption" fontWeight="700" color="text.secondary">NOTES & TERMS:</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{selectedInvoice.notes}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}